import React from 'react';
import * as htmlToImage from 'html-to-image';
import { isEqual } from 'lodash';
import memoize from 'memoize-async';
import { ApLoaderDefault } from '@alixpartners/ui-components';
import { PERMISSIONS } from 'contexts/PermissionsContext';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';
import { downloadAsFile, getHierarchyById } from 'Details/utils';
import { PmiContext } from '../context';
import { ChartOptions } from './ChartOptions';
import { ListTree } from './ListTree';
import { safeSum } from 'Pmi/utils';
import {
	exportTreeData,
	getSpendCategorizationChartDataFromWheel,
} from 'utils/apiRequests';
import {
	getHeroChartParamsFromProps,
	getHeroChartFileExportParamsFromProps,
	getCompaniesWithDefaults,
} from 'Pmi/HeroChart/utils';
import { INDIRECT, DIRECT } from 'Pmi/constants';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import { SectionWrapper, LoadingContainer } from './styles';
import { OrderedItemWrapper } from 'Pmi/styles';
import {
	getConfigIndex,
	isConfigItemVisible,
} from 'components/PersonalizeOrder';
import { CHART_COLORS } from 'styles/colors';
import ContextualMenu from 'Details/ContextualMenu';
import { ContextualMenuBtnWrapper } from './ListBox/styles';

const memoizedApi = {
	getSpendCategorizationChartDataFromWheel: memoize(
		getSpendCategorizationChartDataFromWheel
	),
};

export class CategorySpendTreeChartContainer extends React.Component {
	state = {
		changeStatusModalData: null,
		moveToBinModalData: null,
		chartMinMax: {},
		isLoading: true,
	};

	async componentDidMount() {
		await this.fetchData();
	}

	componentDidUpdate(prevProps) {
		this.fetchDataIfNeeded(prevProps);
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	fetchDataIfNeeded(prevProps) {
		const currentParams = getHeroChartParamsFromProps(this.props);
		const prevParams = getHeroChartParamsFromProps(prevProps);

		if (
			!isEqual(prevParams, currentParams) ||
			prevProps.chartType !== this.props.chartType ||
			prevProps.companies !== this.props.companies ||
			prevProps.pmiBreadcrumbs !== this.props.pmiBreadcrumbs
		) {
			this.fetchData();
		}
	}

	fetchData = async () => {
		if (!this.props.companies || this.props.companies.length === 0) {
			return;
		}

		const params = getHeroChartParamsFromProps(this.props);

		const rawChartData = await this.fetchSpendCategorizationChartDataFromWheel(
			params
		);

		const chartData = rawChartData.map(rawChartDataItem =>
			this.getProcessedChartData(rawChartDataItem, this.props)
		);

		if (this.unmounted) return;

		this.setState({
			chartData,
			rawChartData,
			isLoading: false,
		});
	};

	async fetchSpendCategorizationChartDataFromWheel(params) {
		const api = this.props.memoizedApi || memoizedApi;
		const waiter = api.getWaiterFor
			? await api.getWaiterFor('wheelData')
			: null;

		if (params.categoryLevel === 'L0') {
			const resultData = [];

			const indirectData = await api.getSpendCategorizationChartDataFromWheel({
				...params,
				categoryName: 'Indirect',
				categoryLevel: 'L1',
			});

			if (
				indirectData &&
				indirectData.categorization &&
				indirectData.categorization.length > 0
			) {
				resultData.push(indirectData);
			}

			const directData = await api.getSpendCategorizationChartDataFromWheel({
				...params,
				categoryName: 'Direct',
				categoryLevel: 'L1',
			});

			if (
				directData &&
				directData.categorization &&
				directData.categorization.length > 0
			) {
				resultData.push(directData);
			}

			waiter && waiter.done();

			return resultData;
		}

		const spendCategorizationChartData = await api.getSpendCategorizationChartDataFromWheel(
			params
		);

		waiter && waiter.done();

		return [spendCategorizationChartData];
	}

	getProcessedChartData(rawChartData, props) {
		let { companies, companiesList, showTailSpend, isTailSpend } = props;
		let { categoryName, categorization, tailSpend, parentId } = rawChartData;

		const selectedCompanies = getCompaniesWithDefaults(
			companies,
			companiesList
		);

		let totalItemValue = 0;

		if (!isTailSpend && showTailSpend && tailSpend) {
			categorization = [
				...categorization,
				{
					name: TAIL_SPEND,
					subCategories:
						tailSpend.subCategories && tailSpend.subCategories.length > 0
							? tailSpend.subCategories
							: [
									{
										name: TAIL_SPEND,
										splitByDimension: tailSpend.splitByDimension,
									},
							  ],
				},
			];
		}

		const transformedData = categorization
			.map(categorizationItem => {
				let categorizationItemValue = 0;

				const data = categorizationItem.subCategories
					.map(subCategory => {
						const subcategorySum = selectedCompanies
							.map(companyName => subCategory.splitByDimension[companyName])
							.reduce(safeSum, 0);

						categorizationItemValue += subcategorySum;
						const formattedSubcategorySum =
							this.getFormattedPrice(subcategorySum) || 0;

						return {
							categoryName: subCategory.name || categorizationItem.name,
							id: subCategory.id || categorizationItem.id,
							isTailSpend: categorizationItem.name === TAIL_SPEND,
							values: [
								formattedSubcategorySum,
								...selectedCompanies.map(
									companyName =>
										this.getFormattedPrice(
											subCategory.splitByDimension[companyName]
										) || 0
								),
							],
						};
					})
					.filter(item => !this.isEmptyDataList(item.values));

				const formattedPrice = this.getFormattedPrice(categorizationItemValue);

				totalItemValue += categorizationItemValue;

				return {
					categoryName: categorizationItem.name,
					value: formattedPrice,
					data,
				};
			})
			.filter(item => item.data && item.data.length > 0);

		const formattedTotalPrice = this.getFormattedPrice(totalItemValue);
		const exportId =
			'categorizationSpendTreeChart_' + new Date().valueOf() + (parentId || '');

		return {
			data: transformedData,
			total: {
				categoryName: categoryName,
				value: formattedTotalPrice,
			},
			parentId,
			exportId,
		};
	}

	isEmptyDataList(dataList) {
		const emptyText = this.getFormattedPrice(0);

		return dataList.every(text => text === emptyText);
	}

	sumSplitByDimensions(splitA, splitB) {
		const keys = [...Object.keys(splitA), ...Object.keys(splitB)];
		const uniqKeys = [...new Set(keys)];

		return uniqKeys.reduce(
			(acc, key) => ({ ...acc, [key]: safeSum(splitA[key], splitB[key]) }),
			{}
		);
	}

	getFormattedPrice(value) {
		const { currency, currencySymbol } = this.props;

		const formattedPrice = getFormattedCurrencyWithIdealDenomination({
			value,
			currencyFormat: {
				currency: currency && currency.toUpperCase(),
			},
			isUnitSign: true,
			isCurrencySign: false,
		});

		return `${currencySymbol}${formattedPrice}`;
	}

	handleSetTopChartRef = chartRef => {
		this.chartRef = chartRef;
	};

	handleExportToPng = id => {
		const chart = document.getElementById(id);
		const fileName = 'chart';

		this.setState({ downloadStarted: true });
		htmlToImage.toPng(chart).then(dataUrl => {
			downloadAsFile(dataUrl, fileName);
			this.setState({ downloadStarted: false });
		});
	};

	handleExportData = spendType => {
		const { onDownloadStart, onDownloadSuccess, onDownloadError } = this.props;
		const params = getHeroChartFileExportParamsFromProps(this.props);

		onDownloadStart();
		exportTreeData({
			...params,
			filters: [...(params.filters || []), 'spendType'],
			filterValues: [
				...(params.filterValues || []),
				spendType === 'direct' ? 0 : 1,
			],
		})
			.catch(onDownloadError)
			.finally(onDownloadSuccess);
	};

	handleViewTransactionsClick = item => {
		let { showExplorePage, showTailSpend, isTailSpend } = this.props;
		const options = {};

		const exploreHierarchy = getHierarchyById(item.id);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		if (spendType) {
			options.spendType = spendType.toUpperCase();
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		}

		if (showTailSpend) {
			if (item.isTailSpend) {
				isTailSpend = true;
			}
			options.isTailSpend = isTailSpend;
		}

		showExplorePage(options, item.categoryName);
	};

	handleChangeStatus = item => {
		const { companies } = this.props;
		const data = { extraFilters: { company: companies } };
		const exploreHierarchy = getHierarchyById(item.id);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			data.categoryHierarchy = exploreHierarchy.slice(0, 4);
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	showTopVendorsModal = item => {
		const {
			companies,
			spendType,
			showTailSpend,
			isTailSpend: isTailSpendProp,
		} = this.props;
		const { categoryName, isTailSpend, id } = item;
		const hierarchy = getHierarchyById(id).filter(
			item => ![DIRECT, INDIRECT].includes(item)
		);

		const extraFilters = {
			company: companies,
			...(spendType != null ? { spendType: [spendType] } : {}),
		};

		const data = {
			hierarchy,
			includeTailSpend: !showTailSpend || isTailSpend || isTailSpendProp,
			getTailSpendOnly: isTailSpend || isTailSpendProp,
			name: categoryName,
			extraFilters,
		};

		this.setState({
			topVendorsModalData: data,
		});
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	handleMoveToBin = item => {
		const { companies } = this.props;
		const data = { extraFilters: { company: companies } };
		const exploreHierarchy = getHierarchyById(item.id);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			data.categoryHierarchy = exploreHierarchy.slice(0, 4);
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	getConfigItemTitle = item => {
		if (
			item.parentId &&
			item.parentId.toLowerCase().indexOf('indirect') !== -1
		) {
			return 'indirect';
		}
		return 'direct';
	};

	getConfigItemId = item => {
		if (
			item.parentId &&
			item.parentId.toLowerCase().indexOf('indirect') !== -1
		) {
			return 'CategorySpendTreeIndirect';
		}
		return 'CategorySpendTreeDirect';
	};

	renderContextualMenu = (item, index) => {
		const { permissions } = this.props;
		const { menuOpenItemId } = this.state;
		const hasWritePermissions =
			permissions && permissions.includes(PERMISSIONS.TransactionsWrite);
		const isOpen = menuOpenItemId && menuOpenItemId === item.id;

		return (
			<ContextualMenuBtnWrapper open={isOpen}>
				<ContextualMenu
					onViewTransactions={() =>
						this.handleViewTransactionsClick(item, index)
					}
					hasWritePermissions={hasWritePermissions}
					onChangeStatus={
						!item.isTailSpend ? () => this.handleChangeStatus(item) : undefined
					}
					onShowTopVendors={() => {
						this.showTopVendorsModal(item);
					}}
					toggleMenu={isOpen =>
						this.setState({ menuOpenItemId: isOpen ? item.id : null })
					}
					moveToBin={
						!item.isTailSpend ? () => this.handleMoveToBin(item) : undefined
					}
					isOpen={isOpen}
				/>
			</ContextualMenuBtnWrapper>
		);
	};

	render() {
		const {
			companies,
			companiesList,
			configItems,
			refreshPmiContent,
		} = this.props;
		const {
			changeStatusModalData,
			chartData,
			isLoading,
			topVendorsModalData,
			moveToBinModalData,
		} = this.state;

		const selectedCompanies = getCompaniesWithDefaults(
			companies,
			companiesList
		);

		if (isLoading) {
			return (
				<SectionWrapper>
					<LoadingContainer>
						<ApLoaderDefault />
					</LoadingContainer>
				</SectionWrapper>
			);
		}

		return (
			<React.Fragment>
				{chartData.map((data, index) => {
					const itemId = this.getConfigItemId(data);
					const itemTitle = this.getConfigItemTitle(data);
					return (
						isConfigItemVisible(configItems, itemId) && (
							<OrderedItemWrapper
								id={this.getConfigItemId(data)}
								order={getConfigIndex(configItems, itemId)}
								key={`${data.categoryName}-${index}`}
							>
								<SectionWrapper>
									<ChartOptions
										onPngExportClick={this.handleExportToPng}
										onExportData={() => this.handleExportData(itemTitle)}
										label={'Category spend tree ' + itemTitle}
										exportId={data.exportId}
									/>
									<ListTree
										{...data}
										colors={CHART_COLORS}
										selectedCompanies={selectedCompanies}
										renderContextualMenu={this.renderContextualMenu}
									/>
									{topVendorsModalData && (
										<TopVendorsModal
											isPmi
											onClose={this.hideTopVendorsModal}
											{...topVendorsModalData}
										/>
									)}
									{changeStatusModalData && (
										<ChangeTypeModal
											isOpen={changeStatusModalData}
											isHarmonized
											{...changeStatusModalData}
											onClose={() => {
												this.setState({ changeStatusModalData: null });
											}}
											onSubmit={refreshPmiContent}
											isPmi
										/>
									)}
									{moveToBinModalData && (
										<MoveToBinModal
											isOpen={moveToBinModalData}
											isHarmonized
											{...moveToBinModalData}
											onClose={() => {
												this.setState({ moveToBinModalData: null });
											}}
											onSubmit={refreshPmiContent}
											isPmi
										/>
									)}
								</SectionWrapper>
							</OrderedItemWrapper>
						)
					);
				})}
			</React.Fragment>
		);
	}
}

export default props => (
	<PmiContext.Consumer>
		{({ refreshPmiContent }) => (
			<CategorySpendTreeChartContainer
				{...props}
				refreshPmiContent={refreshPmiContent}
			/>
		)}
	</PmiContext.Consumer>
);
